import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    ArrowLeft, ZoomIn, ZoomOut, Maximize2, Minimize2, Plus,
    User, Bot, Zap, Users, Building, Link2, Search
} from 'lucide-react';
import OrgNodeCard, { OrgNodeMini, getNodeState } from './OrgNodeCard';
import type { Employee } from '../types/api';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';

// ============================================================================
// TYPES
// ============================================================================

interface AugmentedOrgChartProps {
    onNavigate: (view: string) => void;
}

interface TreePosition {
    x: number;
    y: number;
}

// ============================================================================
// DEMO DATA — used when API has no employees yet
// ============================================================================

const DEMO_EMPLOYEES: Employee[] = [
    {
        id: 'ceo-1', companyId: 'demo', name: 'Essam Younes', email: 'essam@polyaero.com',
        title: 'CEO & Founder', department: 'Executive', status: 'ACTIVE',
        replicaLink: { id: 'link-1', employeeId: 'ceo-1', replicaId: 'rep-1', linkType: 'AUGMENTED' },
        createdAt: '', updatedAt: '',
    },
    {
        id: 'cto-1', companyId: 'demo', name: 'Sarah Laurent', email: 'sarah@polyaero.com',
        title: 'CTO', department: 'Engineering', managerId: 'ceo-1', status: 'ACTIVE',
        replicaLink: { id: 'link-2', employeeId: 'cto-1', replicaId: 'rep-2', linkType: 'AUGMENTED' },
        createdAt: '', updatedAt: '',
    },
    {
        id: 'cmo-1', companyId: 'demo', name: 'Marketing AI', email: undefined,
        title: 'Marketing Director', department: 'Marketing', managerId: 'ceo-1', status: 'ACTIVE',
        replicaLink: { id: 'link-3', employeeId: 'cmo-1', replicaId: 'rep-3', linkType: 'AUTONOMOUS' },
        createdAt: '', updatedAt: '',
    },
    {
        id: 'dev-1', companyId: 'demo', name: 'Marc Dupont',
        title: 'Lead Developer', department: 'Engineering', managerId: 'cto-1', status: 'ACTIVE',
        createdAt: '', updatedAt: '',
    },
    {
        id: 'dev-2', companyId: 'demo', name: 'DevOps AI', email: undefined,
        title: 'DevOps Engineer', department: 'Engineering', managerId: 'cto-1', status: 'ACTIVE',
        replicaLink: { id: 'link-4', employeeId: 'dev-2', replicaId: 'rep-4', linkType: 'AUTONOMOUS' },
        createdAt: '', updatedAt: '',
    },
    {
        id: 'support-1', companyId: 'demo', name: 'Julie Martin',
        title: 'Support Lead', department: 'Support', managerId: 'ceo-1', status: 'ACTIVE',
        replicaLink: { id: 'link-5', employeeId: 'support-1', replicaId: 'rep-5', linkType: 'AUGMENTED' },
        createdAt: '', updatedAt: '',
    },
    {
        id: 'support-2', companyId: 'demo', name: 'Support Bot Alpha',
        title: 'L1 Support Agent', department: 'Support', managerId: 'support-1', status: 'ACTIVE',
        replicaLink: { id: 'link-6', employeeId: 'support-2', replicaId: 'rep-6', linkType: 'AUTONOMOUS' },
        createdAt: '', updatedAt: '',
    },
];

// ============================================================================
// TREE HELPERS
// ============================================================================

function buildTree(employees: Employee[]): Employee[] {
    const roots = employees.filter(e => !e.managerId);
    const childMap = new Map<string, Employee[]>();

    employees.forEach(e => {
        if (e.managerId) {
            const children = childMap.get(e.managerId) || [];
            children.push(e);
            childMap.set(e.managerId, children);
        }
    });

    function attachChildren(emp: Employee): Employee {
        const children = childMap.get(emp.id) || [];
        return { ...emp, reports: children.map(attachChildren) };
    }

    return roots.map(attachChildren);
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AugmentedOrgChart({ onNavigate }: AugmentedOrgChartProps) {
    const { currentWorkspace } = useWorkspace();
    const { user } = useAuth();

    // State
    const [employees, setEmployees] = useState<Employee[]>(DEMO_EMPLOYEES);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(DEMO_EMPLOYEES.map(e => e.id)));
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState<TreePosition>({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStart] = useState<TreePosition>({ x: 0, y: 0 });
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterState, setFilterState] = useState<string>('all');
    const [draggedEmployee, setDraggedEmployee] = useState<Employee | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);

    // ── Fetch employees from API ────────────────────────────────────────
    useEffect(() => {
        async function fetchEmployees() {
            try {
                const token = await user?.getIdToken?.();
                if (!token) return;

                const res = await fetch('/api/v1/employees', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.employees?.length > 0) {
                        setEmployees(data.employees);
                        setExpandedNodes(new Set(data.employees.map((e: Employee) => e.id)));
                    }
                }
            } catch {
                // Keep demo data
            }
        }
        fetchEmployees();
    }, [user]);

    // ── Tree data ───────────────────────────────────────────────────────
    const tree = buildTree(employees);

    // ── Stats ───────────────────────────────────────────────────────────
    const stats = {
        total: employees.length,
        human: employees.filter(e => getNodeState(e) === 'HUMAN').length,
        augmented: employees.filter(e => getNodeState(e) === 'AUGMENTED').length,
        autonomous: employees.filter(e => getNodeState(e) === 'AUTONOMOUS').length,
    };
    const augmentationRate = stats.total > 0
        ? Math.round(((stats.augmented + stats.autonomous) / stats.total) * 100)
        : 0;

    // ── Zoom / Pan ──────────────────────────────────────────────────────
    const handleWheel = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setZoom(z => Math.max(0.3, Math.min(2, z + delta)));
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button !== 0) return;
        setIsPanning(true);
        setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }, [pan]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (!isPanning) return;
        setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
    }, [isPanning, panStart]);

    const handleMouseUp = useCallback(() => setIsPanning(false), []);

    // ── Toggle expand ───────────────────────────────────────────────────
    const toggleExpand = useCallback((id: string) => {
        setExpandedNodes(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    // ── Drag & drop ─────────────────────────────────────────────────────
    const handleDragStart = useCallback((_e: React.DragEvent, emp: Employee) => {
        setDraggedEmployee(emp);
    }, []);

    const handleDrop = useCallback((_e: React.DragEvent, target: Employee) => {
        if (!draggedEmployee || draggedEmployee.id === target.id) return;
        // Update the dragged employee's manager to the target
        setEmployees(prev => prev.map(e =>
            e.id === draggedEmployee.id ? { ...e, managerId: target.id } : e
        ));
        setDraggedEmployee(null);
    }, [draggedEmployee]);

    // ── Filter ──────────────────────────────────────────────────────────
    const filteredEmployees = employees.filter(e => {
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            if (!e.name.toLowerCase().includes(q) && !e.title?.toLowerCase().includes(q)) return false;
        }
        if (filterState !== 'all') {
            if (getNodeState(e) !== filterState) return false;
        }
        return true;
    });

    // ── Render tree node recursively ────────────────────────────────────
    const renderNode = (employee: Employee, depth: number = 0): React.ReactNode => {
        const children = employee.reports || [];
        const isExpanded = expandedNodes.has(employee.id);
        const isVisible = filteredEmployees.some(f => f.id === employee.id);

        if (!isVisible && children.length === 0) return null;

        return (
            <div key={employee.id} className="flex flex-col items-center">
                {/* Node card */}
                <div className="relative" style={{ minWidth: depth === 0 ? 280 : 240 }}>
                    <OrgNodeCard
                        employee={employee}
                        isSelected={selectedEmployee?.id === employee.id}
                        isExpanded={isExpanded}
                        hasChildren={children.length > 0}
                        depth={depth}
                        onSelect={setSelectedEmployee}
                        onToggleExpand={() => toggleExpand(employee.id)}
                        onDragStart={handleDragStart}
                        onDrop={handleDrop}
                        compact={depth > 1}
                    />
                </div>

                {/* Children */}
                {children.length > 0 && isExpanded && (
                    <div className="relative mt-4">
                        {/* Vertical connector line */}
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-4 bg-gradient-to-b from-slate-600 to-slate-700" />

                        {/* Horizontal connector */}
                        {children.length > 1 && (
                            <div className="absolute top-4 left-0 right-0 h-px bg-slate-700" style={{
                                marginLeft: `calc(50% / ${children.length})`,
                                marginRight: `calc(50% / ${children.length})`,
                            }} />
                        )}

                        <div className="flex gap-6 pt-4 justify-center">
                            {children.map(child => (
                                <div key={child.id} className="relative">
                                    {/* Vertical line from horizontal connector to child */}
                                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-4 bg-slate-700" />
                                    <div className="pt-4">
                                        {renderNode(child, depth + 1)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ── Detail panel (sidebar) ──────────────────────────────────────────
    const DetailPanel = () => {
        if (!selectedEmployee) return null;
        const state = getNodeState(selectedEmployee);

        return (
            <div className="w-80 bg-slate-800/80 backdrop-blur-xl border-l border-slate-700 flex flex-col overflow-auto">
                {/* Header */}
                <div className="p-5 border-b border-slate-700">
                    <div className="flex items-center gap-3 mb-3">
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-lg
                            ${state === 'HUMAN' ? 'bg-slate-600' :
                              state === 'AUGMENTED' ? 'bg-gradient-to-br from-cyan-500 to-blue-600' :
                              'bg-gradient-to-br from-purple-500 to-indigo-600'}`}>
                            {state === 'AUTONOMOUS' ? <Bot size={28} /> : selectedEmployee.name.charAt(0)}
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white">{selectedEmployee.name}</h3>
                            <p className="text-sm text-slate-400">{selectedEmployee.title || 'Employee'}</p>
                        </div>
                    </div>

                    {/* State badge */}
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold ${
                        state === 'HUMAN' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                        state === 'AUGMENTED' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' :
                        'bg-purple-500/10 text-purple-400 border-purple-500/20'
                    }`}>
                        {state === 'HUMAN' ? <User size={12} /> :
                         state === 'AUGMENTED' ? <Zap size={12} /> : <Bot size={12} />}
                        {state === 'HUMAN' ? 'Human Only' :
                         state === 'AUGMENTED' ? 'AI Augmented' : 'Autonomous AI'}
                    </div>
                </div>

                {/* Details */}
                <div className="p-5 space-y-4 flex-1">
                    {selectedEmployee.email && (
                        <div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1">Email</p>
                            <p className="text-sm text-slate-300">{selectedEmployee.email}</p>
                        </div>
                    )}
                    {selectedEmployee.department && (
                        <div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1">Department</p>
                            <p className="text-sm text-slate-300">{selectedEmployee.department}</p>
                        </div>
                    )}

                    {/* Replica Link Section */}
                    <div className="pt-3 border-t border-slate-700">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2">AI Replica Link</p>
                        {selectedEmployee.replicaLink ? (
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-700/50 border border-slate-600/50">
                                    <Link2 size={14} className="text-cyan-400" />
                                    <div className="flex-1">
                                        <p className="text-xs font-medium text-white">
                                            {selectedEmployee.replicaLink.linkType === 'AUGMENTED' ? 'Augmented' : 'Autonomous'} Link
                                        </p>
                                        <p className="text-[10px] text-slate-400">
                                            Replica ID: {selectedEmployee.replicaLink.replicaId.slice(0, 8)}...
                                        </p>
                                    </div>
                                </div>
                                <button className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-red-500/20 text-red-400 text-xs font-medium hover:bg-red-500/10 transition-colors">
                                    Unlink Replica
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => onNavigate('employee-accounts')}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-cyan-500/20 text-cyan-400 text-xs font-medium hover:bg-cyan-500/10 transition-colors"
                            >
                                <Link2 size={14} />
                                Link AI Replica
                            </button>
                        )}
                    </div>

                    {/* Direct reports */}
                    {selectedEmployee.reports && selectedEmployee.reports.length > 0 && (
                        <div className="pt-3 border-t border-slate-700">
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2">
                                Direct Reports ({selectedEmployee.reports.length})
                            </p>
                            <div className="space-y-1.5">
                                {selectedEmployee.reports.map(r => (
                                    <OrgNodeMini key={r.id} employee={r} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // ════════════════════════════════════════════════════════════════════
    // RENDER
    // ════════════════════════════════════════════════════════════════════

    return (
        <div className={`flex flex-col h-full ${isFullscreen ? 'fixed inset-0 z-50 bg-slate-900' : ''}`}>
            {/* ── Header ───────────────────────────────────────────────── */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700 bg-slate-900/80 backdrop-blur-xl z-10">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => onNavigate('dashboard')}
                        className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        <ArrowLeft size={18} />
                    </button>
                    <div>
                        <h1 className="text-lg font-bold text-white flex items-center gap-2">
                            <Building size={18} className="text-indigo-400" />
                            Organization Chart
                        </h1>
                        <p className="text-xs text-slate-400">
                            {currentWorkspace?.name || 'Company'} — Augmented Workforce
                        </p>
                    </div>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-2">
                    {/* Search */}
                    <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Search..."
                            className="pl-8 pr-3 py-1.5 text-xs bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 w-40"
                        />
                    </div>

                    {/* Filter dropdown */}
                    <select
                        value={filterState}
                        onChange={e => setFilterState(e.target.value)}
                        className="px-2.5 py-1.5 text-xs bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500/50"
                    >
                        <option value="all">All States</option>
                        <option value="HUMAN">Human Only</option>
                        <option value="AUGMENTED">Augmented</option>
                        <option value="AUTONOMOUS">Autonomous</option>
                    </select>

                    {/* Zoom controls */}
                    <div className="flex items-center gap-1 px-1 py-0.5 bg-slate-800 border border-slate-700 rounded-lg">
                        <button onClick={() => setZoom(z => Math.max(0.3, z - 0.1))} className="p-1 text-slate-400 hover:text-white">
                            <ZoomOut size={14} />
                        </button>
                        <span className="text-[10px] text-slate-400 w-8 text-center font-mono">{Math.round(zoom * 100)}%</span>
                        <button onClick={() => setZoom(z => Math.min(2, z + 0.1))} className="p-1 text-slate-400 hover:text-white">
                            <ZoomIn size={14} />
                        </button>
                    </div>

                    {/* Fullscreen */}
                    <button
                        onClick={() => setIsFullscreen(!isFullscreen)}
                        className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                    </button>

                    {/* Add employee  */}
                    <button
                        onClick={() => onNavigate('employee-accounts')}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-lg text-xs font-medium hover:bg-cyan-500/20 transition-colors"
                    >
                        <Plus size={14} /> Add Employee
                    </button>
                </div>
            </div>

            {/* ── Stats Bar ────────────────────────────────────────────── */}
            <div className="flex items-center gap-4 px-5 py-2.5 border-b border-slate-700/50 bg-slate-800/30">
                <div className="flex items-center gap-2">
                    <Users size={14} className="text-slate-400" />
                    <span className="text-xs text-slate-300 font-medium">{stats.total} Total</span>
                </div>
                <div className="w-px h-4 bg-slate-700" />
                <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-blue-400" />
                    <span className="text-xs text-slate-400">{stats.human} Human</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-cyan-400" />
                    <span className="text-xs text-slate-400">{stats.augmented} Augmented</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-purple-400" />
                    <span className="text-xs text-slate-400">{stats.autonomous} Autonomous</span>
                </div>
                <div className="w-px h-4 bg-slate-700" />
                <div className="flex items-center gap-1.5">
                    <Zap size={12} className="text-cyan-400" />
                    <span className="text-xs text-cyan-400 font-semibold">{augmentationRate}% AI Coverage</span>
                </div>
            </div>

            {/* ── Main canvas + detail panel ────────────────────────────── */}
            <div className="flex-1 flex min-h-0">
                {/* Canvas */}
                <div
                    ref={containerRef}
                    className="flex-1 overflow-hidden cursor-grab active:cursor-grabbing relative"
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    {/* Grid background */}
                    <div className="absolute inset-0 opacity-[0.03]"
                        style={{
                            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
                            backgroundSize: '24px 24px',
                        }}
                    />

                    {/* Tree container */}
                    <div
                        className="min-w-full min-h-full flex justify-center pt-12 pb-24"
                        style={{
                            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                            transformOrigin: 'top center',
                            transition: isPanning ? 'none' : 'transform 0.15s ease-out',
                        }}
                    >
                        <div className="flex flex-col items-center gap-0">
                            {tree.map(root => renderNode(root, 0))}

                            {/* Add root node button */}
                            {tree.length === 0 && (
                                <div className="text-center py-20">
                                    <div className="w-20 h-20 rounded-2xl bg-slate-800 border-2 border-dashed border-slate-600 flex items-center justify-center mx-auto mb-4">
                                        <Users size={32} className="text-slate-500" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Build Your Org Chart</h3>
                                    <p className="text-sm text-slate-400 mb-6 max-w-sm mx-auto">
                                        Add employees and replicas to visualize your augmented workforce.
                                    </p>
                                    <button
                                        onClick={() => onNavigate('employee-accounts')}
                                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-500 hover:bg-cyan-400 text-slate-900 rounded-xl font-semibold text-sm transition-colors"
                                    >
                                        <Plus size={16} /> Add First Employee
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Detail panel (sidebar) */}
                {selectedEmployee && <DetailPanel />}
            </div>
        </div>
    );
}
